import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StrikeZoneType } from 'lib_ts/enums/machine.enums';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { useEffect, useMemo, useState } from 'react';
import { SessionEventsService } from 'services/session-events.service';

interface ISessionRecapHits {
  szDef: IStrikeZone;
  /** every hit in the session, derived */
  records: ISessionRecapHit[];
  /** records with pitch data, which is what the recap displays */
  filtered: ISessionRecapHit[];
  byID: Record<string, ISessionRecapHit>;
}

export const useSessionRecapHits = (): ISessionRecapHits => {
  const [heightFt, setHeightFt] = useState<number | undefined>();

  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const hits = useAnalyticsStore((s) => s.hits);

  // todo: this should just lookup the hitter
  const fireID = useAnalyticsStore(
    (s) => s.hits.find((h) => h.fire?._id)?.fire._id
  );

  // get the height from the first fire in the session
  useEffect(() => {
    if (!fireID) {
      setHeightFt(undefined);
      return;
    }

    let cancelled = false;
    setHeightFt(undefined);

    SessionEventsService.getFireByID(fireID).then((fire) => {
      if (cancelled) {
        return;
      }

      const height = fire?.data?.hitterExt?.height_ft;

      setHeightFt(
        typeof height === 'number' && !Number.isNaN(height) ? height : undefined
      );
    });

    return () => {
      cancelled = true;
    };
  }, [fireID]);

  const szDef = useMemo(() => {
    switch (sessionMachine?.strikeZoneType) {
      case StrikeZoneType.MlbAbs: {
        return PlateHelper.getMlbAbsStrikeZone(heightFt);
      }
      case StrikeZoneType.Stringer: {
        return PlateHelper.getStringerStrikeZone(heightFt);
      }
      default: {
        return VISIBLE_STRIKEZONE;
      }
    }
  }, [heightFt, sessionMachine?.strikeZoneType]);

  const records = useMemo(
    () => SessionRecapHelper.convertToRecap(hits, szDef),
    [hits, szDef]
  );

  const filtered = useMemo(
    () => records.filter((r) => !!r.hit.pitch?._id),
    [records]
  );

  const byID = useMemo(
    () =>
      records.reduce(
        (acc, r) => {
          acc[r.hit._id] = r;
          return acc;
        },
        {} as Record<string, ISessionRecapHit>
      ),
    [records]
  );

  return { szDef, records, filtered, byID };
};
